import { useState } from 'react';
import { Alert, Box, Button, FormField, Input, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import {
  PRIORITY_BG,
  PRIORITY_BORDER,
  PRIORITY_LEGEND_BG,
  type PriorityTier,
} from '../../../utils/priority';
import { labelToPriority, type SrOLabel } from '../utils/priorityCycle';

interface NewSpanUcModalProps {
  industry: string;
  /** Real (non-`_span_`) area names, used for the coverage checkbox list. */
  areaNames: string[];
  saving: boolean;
  onClose: () => void;
  /** Resolves `null` on success, or a ready-to-show failure message (already correct
   *  about retry-ability — a 403 must not be reported as retryable). */
  onSave: (input: {
    useCase: string;
    priority: number;
    coveredAreas: string[];
  }) => Promise<string | null>;
}

const OPTIONS: { value: SrOLabel; tier: PriorityTier; label: string }[] = [
  { value: 'S', tier: 'strategic', label: 'Strategic' },
  { value: 'R', tier: 'recommended', label: 'Recommended' },
  { value: 'O', tier: 'opportunistic', label: 'Opportunistic' },
];

export default function NewSpanUcModal({
  industry,
  areaNames,
  saving,
  onClose,
  onSave,
}: NewSpanUcModalProps) {
  const [useCase, setUseCase] = useState('');
  const [priority, setPriority] = useState<SrOLabel>('R');
  const [covered, setCovered] = useState<Set<string>>(new Set(areaNames));
  const [error, setError] = useState<string | null>(null);

  const toggle = (area: string) => {
    setCovered((prev) => {
      const next = new Set(prev);
      if (next.has(area)) next.delete(area);
      else next.add(area);
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    const name = useCase.trim();
    if (!name) {
      setError('UC 名称不能为空');
      return;
    }
    if (covered.size === 0) {
      setError('至少勾选一个 Solution Area');
      return;
    }
    const failure = await onSave({
      useCase: name,
      priority: labelToPriority(priority),
      coveredAreas: areaNames.filter((a) => covered.has(a)),
    });
    if (failure) setError(failure);
  };

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!saving) onClose();
      }}
      header={`新建横跨 UC（${industry}）`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={!useCase.trim() || covered.size === 0}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="small">
          创建一个横跨多个 Solution Area 的 UC（数据上 solutionArea=&quot;_span_&quot;）
        </Box>

        <FormField label="Use Case Name *">
          <Input
            value={useCase}
            onChange={({ detail }) => setUseCase(detail.value)}
            disabled={saving}
            autoFocus
          />
        </FormField>

        <FormField label="Priority *">
          <div className="ivt-priority-radio-group" role="radiogroup" aria-label="优先级">
            {OPTIONS.map((opt) => {
              const selected = priority === opt.value;
              return (
                <label
                  key={opt.value}
                  className={
                    'ivt-priority-radio' + (selected ? ' ivt-priority-radio--selected' : '')
                  }
                  style={{
                    background: PRIORITY_BG[opt.tier],
                    borderColor: selected
                      ? PRIORITY_LEGEND_BG[opt.tier]
                      : PRIORITY_BORDER[opt.tier],
                  }}
                >
                  <input
                    type="radio"
                    name="newspan-priority"
                    value={opt.value}
                    checked={selected}
                    disabled={saving}
                    onChange={() => setPriority(opt.value)}
                  />
                  <span
                    className="ivt-priority-radio__chip"
                    style={{ background: PRIORITY_LEGEND_BG[opt.tier] }}
                  >
                    {opt.value}
                  </span>
                  <span className="ivt-priority-radio__label">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </FormField>

        <FormField label="Covered Areas *">
          <div className="ivt-coverage-list">
            {areaNames.length === 0 ? (
              <Box variant="small" color="text-status-inactive">该行业暂无可勾选的 Solution Area</Box>
            ) : (
              areaNames.map((area) => (
                <label key={area} className="ivt-coverage-item">
                  <input
                    type="checkbox"
                    checked={covered.has(area)}
                    disabled={saving}
                    onChange={() => toggle(area)}
                  />
                  <span>{area}</span>
                </label>
              ))
            )}
          </div>
        </FormField>

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
